import React from 'react';
import Header from './component/Header';
import PublicFooter from '../components/layout/PublicFooter';
import StaticPageLayout from '../components/layout/StaticPageLayout';
import { Button, Card } from '../components/ui';
import SEO from '../components/SEO';

const helpSections = [
    {
        icon: (
            <svg className="tw-w-6 tw-h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
            </svg>
        ),
        title: 'Creating Your Account',
        content: [
            'Visit the home page and enter your county, license plate number, email, and phone number.',
            'On the next step, add your plate details including the expiration date. You can add multiple plates at once.',
            'Some states require additional information (such as the last digits of your VIN) — these fields appear automatically.',
            'Finally, enter your billing address, create a password, and set up your payment method (ACH or credit/debit card).',
            'Once submitted, your account is created and your plates are registered for automatic renewal.',
        ],
    },
    {
        icon: (
            <svg className="tw-w-6 tw-h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182M2.985 19.644l3.181-3.182" />
            </svg>
        ),
        title: 'How Automatic Renewal Works',
        content: [
            'About 90 days before your plate expires, we contact the state to get your exact renewal cost.',
            'You receive a notification (email/SMS) about 30 days before expiration with the charge amount and date.',
            'Approximately 3 weeks before expiration, your preferred payment method is charged automatically.',
            'We submit the renewal to the state on your behalf after successful payment.',
            'Your new registration tags are mailed to your address on file.',
            'After completion, your expiration date is extended by one year and the cycle repeats.',
        ],
    },
    {
        icon: (
            <svg className="tw-w-6 tw-h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
            </svg>
        ),
        title: 'Managing Payment Methods',
        content: [
            'Navigate to "Payment Methods" from your account menu to view all saved methods.',
            'Click "Add More" to add a new ACH (bank account) or credit/debit card.',
            'Use the "Make Default" toggle to set which method is used for new plates.',
            'Use the "Enabled" toggle to activate or deactivate a payment method.',
            'Each plate can use a different payment method — change it from the dashboard by clicking "Change" next to the payment method.',
        ],
    },
    {
        icon: (
            <svg className="tw-w-6 tw-h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
            </svg>
        ),
        title: 'Notification Preferences',
        content: [
            'Go to "Profile" then the "Saved Contacts" tab to manage your notification methods.',
            'Add contacts with phone numbers (for SMS) and email addresses.',
            'Each plate can have its own preferred contact — change it from the dashboard.',
            'You will receive notifications for: upcoming charges, successful payments, failed payments, and renewal completion.',
            'You must keep at least one contact on your account at all times.',
        ],
    },
    {
        icon: (
            <svg className="tw-w-6 tw-h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z" />
            </svg>
        ),
        title: 'Using Your Dashboard',
        content: [
            'Your dashboard shows all registered plates with their expiration countdown, payment method, and contact preference.',
            'Toggle the status switch to activate or deactivate auto-renewal for any plate.',
            'Click "Change" next to the contact or payment method to update preferences for a specific plate.',
            'Click on a plate number to view its complete renewal and transaction history.',
            'Use "Add More" to register additional plates to your account.',
        ],
    },
    {
        icon: (
            <svg className="tw-w-6 tw-h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
        ),
        title: 'Troubleshooting',
        items: [
            { title: 'Charge failed', detail: 'Check that your payment method is enabled and has sufficient funds. We retry daily for up to 3 attempts. After 3 failures, update your payment method and reactivate the plate.' },
            { title: "Haven't received tags", detail: 'Tags are mailed by the state after renewal is processed. Allow 2-3 weeks. Verify your mailing address is correct in your profile. Contact us if it has been longer.' },
            { title: 'Renewal amount seems wrong', detail: "The renewal cost comes directly from the state's system and includes all state and county fees. The RMT service fee is separate. Contact support if you believe there is an error." },
            { title: 'Want to stop auto-renewal', detail: 'Toggle the plate to "Inactive" on your dashboard. If a charge has already been processed, contact support for assistance.' },
            { title: "Can't log in", detail: 'Use the "Forgot Password" option from the Sign In popup. If you continue to have issues, contact support at hello@renewmytags.com.' },
            { title: 'Renewed manually at the DMV', detail: 'Deactivate the plate on your dashboard immediately to prevent a duplicate charge. If a charge was already made, contact support.' },
        ],
    },
];

const HelpCenter = () => {
    return (
        <div style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>
            <SEO title="Help Center" description="Step-by-step guides for using RMT: account setup, adding plates, managing payment methods, understanding renewals, and troubleshooting common issues." canonical="/help" />
            <Header />
            <StaticPageLayout
                title="Help Center"
                subtitle="Step-by-step guides to help you get the most out of your Renew My Tech account."
            >
                {/* Quick Links Grid */}
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-3 tw-mb-10 tw-max-w-5xl">
                    {helpSections.map((section, idx) => (
                        <a
                            key={idx}
                            href={`#section-${idx}`}
                            className="tw-flex tw-items-center tw-gap-3 tw-p-4 tw-bg-gray-50 tw-rounded-xl tw-border tw-border-gray-200 hover:tw-border-primary-300 hover:tw-bg-primary-50 tw-transition-all tw-duration-200 tw-no-underline tw-group"
                        >
                            <div className="tw-text-primary-500 group-hover:tw-text-primary-600 tw-transition-colors">
                                {section.icon}
                            </div>
                            <span className="tw-text-sm tw-font-medium tw-text-gray-700 group-hover:tw-text-primary-700">
                                {section.title}
                            </span>
                        </a>
                    ))}
                </div>

                {/* Detailed Sections */}
                <div className="tw-space-y-8 tw-max-w-4xl">
                    {helpSections.map((section, idx) => (
                        <div key={idx} id={`section-${idx}`} className="tw-scroll-mt-24">
                            <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4 tw-pb-3 tw-border-b-2 tw-border-primary-100">
                                <div className="tw-text-primary-500">{section.icon}</div>
                                <h2 className="tw-text-2xl tw-text-gray-900 tw-m-0">{section.title}</h2>
                            </div>

                            {section.content && (
                                <ol className="tw-list-decimal tw-pl-5 tw-space-y-3">
                                    {section.content.map((step, sIdx) => (
                                        <li key={sIdx} className="tw-text-sm tw-text-gray-600 tw-leading-relaxed tw-pl-2">
                                            {step}
                                        </li>
                                    ))}
                                </ol>
                            )}

                            {section.items && (
                                <div className="tw-space-y-3">
                                    {section.items.map((item, iIdx) => (
                                        <div key={iIdx} className="tw-p-4 tw-bg-gray-50 tw-rounded-xl tw-border-l-4 tw-border-l-primary-500">
                                            <h4 className="tw-text-sm tw-font-semibold tw-text-gray-800 tw-m-0 tw-mb-1">{item.title}</h4>
                                            <p className="tw-text-sm tw-text-gray-600 tw-m-0 tw-leading-relaxed">{item.detail}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Bottom CTA */}
                <div className="tw-text-center tw-bg-gray-50 tw-rounded-2xl tw-p-8 tw-mt-12 tw-max-w-4xl">
                    <h3 className="tw-text-2xl tw-text-gray-900 tw-m-0 tw-mb-2">Need more help?</h3>
                    <p className="tw-text-sm tw-text-gray-500 tw-mb-6 tw-m-0">
                        Check our FAQ for quick answers or reach out to our support team directly.
                    </p>
                    <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-3">
                        <Button variant="primary" to="/faq">View FAQ</Button>
                        <Button variant="secondary" to="/contact/us">Contact Support</Button>
                    </div>
                </div>
            </StaticPageLayout>
            <PublicFooter />
        </div>
    );
};

export default HelpCenter;
